/**
 * Unified data provider for online (API) and standalone (bundled JSON) modes.
 *
 * When REACT_APP_STANDALONE=true, loads data from /game-data/bundle.json.
 * Otherwise, fetches from the live API (existing behaviour).
 */

import { API_BASE_URL } from '../../utils/api';
import { rewriteGameAssetPaths } from './assetUrl';

const IS_STANDALONE = process.env.REACT_APP_STANDALONE === 'true';
const STANDALONE_TEAM_ID = process.env.REACT_APP_TEAM_ID || '';

let _bundle = null;

/**
 * Returns the effective teamId.
 * In standalone mode, uses REACT_APP_TEAM_ID or the teamId from the bundle.
 */
export function getStandaloneTeamId() {
  if (_bundle?.teamId) return _bundle.teamId;
  return STANDALONE_TEAM_ID;
}

export function isStandalone() {
  return IS_STANDALONE;
}

/**
 * Load all game data. Returns an object with the same shape regardless of mode:
 * {
 *   gameConfig, storyflows, characters, instaAccounts, musicTracks
 * }
 */
export async function loadGameData(teamId) {
  if (IS_STANDALONE) {
    return loadFromBundle();
  }
  return loadFromApi(teamId);
}

// ── Standalone: load from embedded JSON ──────────────────────────────

async function loadFromBundle() {
  if (_bundle) return _bundle;
  // Relative path — resolved against <base href="./"> in index.html,
  // which is pinned to the PWA directory. Works regardless of how the
  // PWA is hosted (versioned R2 folder, custom domain root, Tauri
  // asset:// scheme, anything) because the resolution never touches a
  // host-absolute prefix.
  const res = await fetch('game-data/bundle.json');
  if (!res.ok) throw new Error('Failed to load game bundle');
  const data = await res.json();
  // The admin exports characters/storyflows/insta posts with host-
  // absolute '/game-assets/…' publicUrls. Convert each to its relative
  // 'game-assets/…' counterpart in one pass so every downstream
  // consumer (img src, audio src, etc.) gets a path that plays nicely
  // with the base href.
  rewriteGameAssetPaths(data);
  _bundle = data;
  return data;
}

// ── Online: load from API (mirrors existing GameShell logic) ─────────

async function loadFromApi(teamId) {
  const token = sessionStorage.getItem('flowcore_session_token') || '';
  const headers = { Authorization: `Bearer ${token}` };

  const [teamRes, sfRes, charRes, instaRes] = await Promise.all([
    fetch(`${API_BASE_URL}/api/teams/my-teams`, { headers }),
    fetch(`${API_BASE_URL}/api/storyflows?teamId=${encodeURIComponent(teamId)}&includeGraph=true`, { headers }),
    fetch(`${API_BASE_URL}/api/characters?teamId=${encodeURIComponent(teamId)}`, { headers }),
    fetch(`${API_BASE_URL}/api/insta?teamId=${encodeURIComponent(teamId)}`, { headers }).catch(() => null)
  ]);

  let gameConfig = {};
  if (teamRes.ok) {
    const data = await teamRes.json();
    const team = (data.teams || []).find((t) => t.id === teamId);
    if (team?.gameConfig) gameConfig = team.gameConfig;
  }

  let storyflows = [];
  if (sfRes.ok) {
    const data = await sfRes.json();
    storyflows = (data.storyflows || []).sort((a, b) => (a.number ?? 9999) - (b.number ?? 9999));
  }

  let characters = [];
  if (charRes.ok) {
    const data = await charRes.json();
    characters = data.characters || [];
  }

  let instaAccounts = [];
  if (instaRes && instaRes.ok) {
    const data = await instaRes.json();
    const accs = data.accounts || [];
    instaAccounts = await Promise.all(accs.map(async (acc) => {
      try {
        const postsRes = await fetch(`${API_BASE_URL}/api/insta/${acc.id}/posts`, { headers });
        if (postsRes.ok) {
          const pd = await postsRes.json();
          return { ...acc, posts: pd.posts || [] };
        }
      } catch (_) {}
      return { ...acc, posts: [] };
    }));
  }

  return {
    teamId,
    gameConfig,
    storyflows,
    characters,
    instaAccounts
  };
}
